import * as msgpack from 'msgpack-lite';
import { decodeUTF8Bytes } from '../utils/string';

declare module 'msgpack-lite' {
  class Decoder {
    public on(event: string, listener: (...args: any[]) => void): this;
    public on(event: 'close', listener: () => void): this;
    public on(event: 'data', listener: (chunk: Uint8Array | string | object) => void): this;
    public on(event: 'end', listener: () => void): this;
    public on(event: 'readable', listener: () => void): this;
    public on(event: 'error', listener: (err: Error) => void): this;
  }
}

const signature = new Uint8Array([0xF0, 0x9F, 0x95, 0xAE]);

export interface Metadata {
  version: number;
  title: string;
}

export interface Attachment {
  filename: string;
  data: Uint8Array;
}

export interface UnpackedWorkspaceData {
  metadata: Metadata;
  main: object;
  attachments: Attachment[];
}

namespace IntermediateData {
  export interface AttachmentFileHeader {
    type: 'attachment';
    filename: string;
  }

  export interface Attachment {
    filename: string;
    data?: Uint8Array;
  }
}

class UnpackingProcessor {
  private chunkIndex: number;
  private metadata: Metadata | undefined;
  private maindata: object | undefined;
  private attachments: IntermediateData.Attachment[];

  public constructor() {
    this.chunkIndex = 0;
    this.metadata = undefined;
    this.maindata = undefined;
    this.attachments = [];
  }

  public handleData = (data: any): void => {
    this.chunkIndex += 1;

    if (this.chunkIndex === 1) {
      if (data instanceof Uint8Array) {
        throw new Error('Invalid file structure: first data must be a map.');
      }
      this.handleMetadata(data as Metadata);
    } else if (this.chunkIndex === 2) {
      // Nothing to do. Just validation.
      if (data.type !== 'main') {
        throw new Error('Invalid file structure: main.json must be the first file.');
      }
    } else if (this.chunkIndex === 3) {
      this.handleMainBody(data as Uint8Array);
    } else if (this.chunkIndex % 2 === 0) {
      if (data instanceof Uint8Array) {
        throw new Error('Invalid file structure: this data must be a map.');
      }
      const attachmentIndex = (this.chunkIndex - 4) / 2;
      this.handleFileHeader(data as IntermediateData.AttachmentFileHeader, attachmentIndex);
    } else {
      if (!(data instanceof Uint8Array)) {
        throw new Error('Invalid file structure: this data must be a Uint8Array.');
      }
      const attachmentIndex = (this.chunkIndex - 5) / 2;
      return this.handleFileBody(data as Uint8Array, attachmentIndex);
    }
  }

  public get result(): Readonly<UnpackedWorkspaceData> {
    if (this.metadata === undefined) {
      throw new Error('Invalid file structure: metadata has not been given.');
    }
    if (this.maindata === undefined) {
      throw new Error('Invalid file structure: main.json has not been given.');
    }
    for (const attachment of this.attachments) {
      if (attachment.data === undefined) {
        throw new Error('Invalid file structure: insufficient attachment data.');
      }
    }
    return {
      metadata: this.metadata,
      main: this.maindata,
      attachments: this.attachments as Attachment[],
    };
  }

  private handleMetadata = (data: Metadata): void => {
    this.metadata = data;
  }

  private handleMainBody = (data: Uint8Array): void => {
    const json = decodeUTF8Bytes(data);
    this.maindata = JSON.parse(json);
  }

  private handleFileHeader = (data: IntermediateData.AttachmentFileHeader, index: number): void => {
    if (this.attachments.length !== index) {
      throw new Error('Unexpected error.');
    }
    this.attachments[index] = { filename: data.filename };
  }

  private handleFileBody = (data: Uint8Array, index: number): void => {
    this.attachments[index].data = data;
  }
}

function checkSignature(data: ArrayBuffer): boolean {
  const actual = new Uint8Array(data.slice(0, 4));
  if (actual.length !== signature.length) {
    return false;
  }
  for (let i = 0; i < actual.length; i += 1) {
    if (actual[i] !== signature[i]) {
      return false;
    }
  }
  return true;
}

export function unpackWorkspace(data: ArrayBuffer): UnpackedWorkspaceData {
  if (!checkSignature(data)) {
    throw new Error('Unsupported file type');
  }

  const decoder = new msgpack.Decoder();
  const processor = new UnpackingProcessor();
  decoder.on('data', (data) => {
    processor.handleData(data);
  });
  decoder.decode(new Uint8Array(data.slice(4)));

  return processor.result;
}
